/**
 * PipelineZeroState — shown for a pipelineable entity type (e.g. deals) that
 * has no pipeline yet.
 *
 * Until a pipeline with stages exists there is nothing to add records to, so
 * we replace the list/board (and its Add button) with a focused empty state
 * whose CTA slides out the onboarding `create_pipeline` proposal for this
 * entity type. If that proposal is no longer available, we fall back to the
 * pipeline settings where one can be created from scratch.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { useCanWrite } from '@/hooks/usePermissions';
import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { useCreatePipelineProposal } from '@/lib/crm/hooks/useCreatePipelineProposal';
import { PipelineIcon } from '@/lib/crm/utils/icons/CustomIcons';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';

export interface PipelineZeroStateProps {
  orgId: number;
  /** Entity type the pipeline is for, e.g. `ahoy_deal`. */
  entityTypeName: string;
  /** Lowercase singular label, e.g. "deal". */
  entityLabel: string;
  /** Lowercase plural label, e.g. "deals". */
  entityPluralLabel: string;
}

export function PipelineZeroState({
  orgId,
  entityTypeName,
  entityLabel,
  entityPluralLabel,
}: PipelineZeroStateProps) {
  const navigate = useNavigate();
  const { openProposal } = useProposalSidebar();
  // Setting up a pipeline is a write (a proposal, or the pipeline settings).
  // A read-only seat gets a plain "nothing here yet" — never a CTA that ends
  // in a refusal.
  const canWrite = useCanWrite();
  const { proposalId, isLoading } = useCreatePipelineProposal(
    orgId,
    entityTypeName
  );

  // Pair the entity's own icon (e.g. the deal icon) with the pipeline icon so
  // the empty state reads as "<entity> + pipeline".
  const EntityIcon = getEntityTypeIconComponent(entityTypeName);

  const handleCreatePipeline = useCallback(() => {
    if (proposalId !== undefined) {
      openProposal(proposalId);
      return;
    }
    // No proposal to slide out (e.g. it was already resolved) — fall back to
    // the pipeline settings where a pipeline can be created from scratch.
    navigate(`/${orgId}/settings/pipelines`);
  }, [proposalId, openProposal, navigate, orgId]);

  return (
    <div className='flex h-full w-full items-center justify-center p-8'>
      <EmptyState
        icon={
          <div className='flex items-center gap-3'>
            <FeaturedIcon
              icon={EntityIcon}
              size='xl'
              theme='light-outline'
              color='brand'
            />
            <FeaturedIcon
              icon={PipelineIcon}
              size='xl'
              theme='light-outline'
              color='brand'
            />
          </div>
        }
        title={
          canWrite
            ? `Set up your ${entityLabel} pipeline`
            : `No ${entityLabel} pipeline yet`
        }
        description={
          canWrite
            ? `Create a pipeline with stages to start tracking ${entityPluralLabel}. You can add ${entityPluralLabel} once a pipeline exists.`
            : `Once your team sets up a pipeline, ${entityPluralLabel} will appear here.`
        }
        size='lg'
        action={
          canWrite
            ? {
                label: 'Create a pipeline',
                onClick: handleCreatePipeline,
                color: 'primary',
                icon: PipelineIcon,
                isDisabled: isLoading,
                disabledTooltip: 'Loading…',
              }
            : undefined
        }
      />
    </div>
  );
}
